import { AWProposalV2, Proposal_TEMP } from '@blk/aw-api-clients';
import { BfmProposalAnalysisResponseV3 } from '@blk/aw-bff-typescript-models';
import { LotConstraint, OptimizationContext, PositionConstraint } from '@blk/awt-aladdinportfoliobuilderserver';
import { isNil } from 'lodash';
import {
  LotConstraintsMap,
  PositionConstraintsMap,
  ProposedRealizedGainLossLotMap,
  UnrealizedGainLossLotMap,
} from './Models';

export const buildUniquePositionKey = (portfolioId: string, positionId: string) => `${portfolioId}#${positionId}`;

export const buildUniqueLotKey = (portfolioId: string, positionId: string, lotId: string) =>
  `${portfolioId}#${positionId}#${lotId}`;

export const collectProposedRealizedGainLossMap = (
  proposalAnalysis: BfmProposalAnalysisResponseV3
): ProposedRealizedGainLossLotMap => {
  const proposedRealizedGainLossMap: ProposedRealizedGainLossLotMap = {};
  proposalAnalysis.taxAnalytics?.proposed.portfolios.forEach((portfolio) =>
    portfolio.gainLoss?.proposedRealized.forEach((gainLoss) => {
      gainLoss.contributors.forEach((contributor) => {
        proposedRealizedGainLossMap[buildUniqueLotKey(portfolio.id, contributor.positionId, contributor.lotId)] = {
          proposedRealizedGainLoss: contributor.gainLoss,
          gainLossType: gainLoss.type,
        };
      });
    })
  );
  return proposedRealizedGainLossMap;
};

export const collectUnrealizedGainLossMap = (
  proposalAnalysis: BfmProposalAnalysisResponseV3
): UnrealizedGainLossLotMap => {
  const unrealizedGainLossLotMap: UnrealizedGainLossLotMap = {};
  proposalAnalysis.taxAnalytics?.original.portfolios.forEach((portfolio) =>
    portfolio.gainLoss?.unrealized.forEach((gainLoss) => {
      gainLoss.contributors.forEach((contributor) => {
        unrealizedGainLossLotMap[buildUniqueLotKey(portfolio.id, contributor.positionId, contributor.lotId)] = {
          unrealizedGainLoss: contributor.gainLoss,
          gainLossType: gainLoss.type,
        };
      });
    })
  );
  return unrealizedGainLossLotMap;
};

export const buildPositionConstraintsMap = (
  optimizationContext: OptimizationContext | undefined
): PositionConstraintsMap => {
  // Use foreach instead of reduce for performance reasons
  const lockMap: PositionConstraintsMap = {};
  optimizationContext?.portfolioConstraints.forEach((constraint) => {
    constraint.positionConstraints.forEach((posConstraint) => {
      const posKey = buildUniquePositionKey(constraint.portfolioId, posConstraint.positionId);
      lockMap[posKey] = posConstraint;
    });
  });
  return lockMap;
};

export const buildLotConstraintsMap = (
  proposal: Pick<AWProposalV2 | Proposal_TEMP, 'optimizationContext'>
): LotConstraintsMap => {
  // Use foreach instead of reduce for performance reasons
  const lockMap: LotConstraintsMap = {};
  proposal.optimizationContext?.portfolioConstraints.forEach((constraint) => {
    constraint.lotConstraints.forEach((lotConstraint) => {
      const lotKey = buildUniqueLotKey(constraint.portfolioId, lotConstraint.positionId, lotConstraint.lotId);
      lockMap[lotKey] = lotConstraint;
    });
  });
  return lockMap;
};

export const isPositionConstraintLocked = (positionConstraint: PositionConstraint | undefined) =>
  positionConstraint?.lockType === 'full';

export const isPositionConstraintSellLocked = (constraint: PositionConstraint | undefined) =>
  constraint?.lockType === 'sell';

export const isLotConstraintLocked = (lotConstraint: LotConstraint | undefined): boolean =>
  !!(lotConstraint?.sellLock && !isNil(lotConstraint?.metadata?.reason));
